import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bold, Link2, Mail, Pilcrow, Plus, Send, Trash2 } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Spinner,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { NewsletterListItem, NewsletterStatusKey } from '@pnl/shared';
import { api } from '../lib/api';

const statusTone: Record<
  NewsletterStatusKey,
  'neutral' | 'brand' | 'success' | 'warning' | 'danger'
> = {
  DRAFT: 'neutral',
  SCHEDULED: 'warning',
  SENDING: 'brand',
  SENT: 'success',
  FAILED: 'danger',
};
const statusLabel: Record<NewsletterStatusKey, string> = {
  DRAFT: 'Rascunho',
  SCHEDULED: 'Agendado',
  SENDING: 'Enviando',
  SENT: 'Enviado',
  FAILED: 'Falhou',
};

function errMsg(e: unknown, fallback: string): string {
  return e instanceof PnlApiError ? e.api.message : fallback;
}

export function ComunicadosPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'newsletters'],
    queryFn: () => api.listNewsletters({ page: 1, pageSize: 50 } as never),
    refetchInterval: 15_000,
  });

  if (editing) {
    return (
      <Composer
        id={editing}
        onClose={() => {
          setEditing(null);
          void qc.invalidateQueries({ queryKey: ['admin', 'newsletters'] });
        }}
      />
    );
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Comunicação
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Comunicados
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Informativos pra base de traders (ativos com e-mail verificado). Quem cancela a
            inscrição não recebe; os e-mails essenciais (contrato, pagamento) não são afetados.
          </p>
        </div>
        <Button leadingIcon={<Plus size={15} />} onClick={() => setEditing('new')}>
          Novo comunicado
        </Button>
      </div>

      <Card>
        <CardBody>
          {list.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Mail size={28} />}
              title="Nenhum comunicado ainda"
              description="Crie o primeiro informativo pra base."
            />
          ) : (
            <ul className="divide-ink-100 divide-y">
              {items.map((n) => (
                <NewsletterRow key={n.id} item={n} onOpen={() => setEditing(n.id)} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function NewsletterRow({ item, onOpen }: { item: NewsletterListItem; onOpen: () => void }) {
  const date = item.sentAt ?? item.scheduledFor ?? item.createdAt;
  return (
    <li className="flex items-center justify-between gap-4 py-3.5 first:pt-0 last:pb-0">
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <p className="text-ink-900 truncate text-sm font-semibold">{item.subject}</p>
          <Badge tone={statusTone[item.status]}>{statusLabel[item.status]}</Badge>
        </div>
        <p className="text-ink-500 mt-0.5 text-xs">
          {item.status === 'SENT'
            ? `${item.sentCount} enviado(s) · abertura ${item.openRate ?? 0}%`
            : item.status === 'SCHEDULED'
              ? `Agendado para ${new Date(item.scheduledFor!).toLocaleString('pt-BR')}`
              : `Criado em ${new Date(item.createdAt).toLocaleDateString('pt-BR')}`}
          {' · '}
          {new Date(date).toLocaleDateString('pt-BR')}
        </p>
      </button>
      <Button size="sm" variant="secondary" onClick={onOpen}>
        Abrir
      </Button>
    </li>
  );
}

const EDITOR_BTN =
  'border-ink-200 text-ink-700 hover:bg-ink-100 inline-flex h-8 w-8 items-center justify-center rounded-md border';

function Composer({ id, onClose }: { id: string | 'new'; onClose: () => void }) {
  const isNew = id === 'new';
  const qc = useQueryClient();
  const editorRef = useRef<HTMLDivElement>(null);

  const detail = useQuery({
    queryKey: ['admin', 'newsletter', id],
    queryFn: () => api.getNewsletter(id as string),
    enabled: !isNew,
  });

  const [subject, setSubject] = useState('');
  const [savedId, setSavedId] = useState<string | null>(isNew ? null : (id as string));
  const [initialHtml, setInitialHtml] = useState('');
  const [ready, setReady] = useState(isNew);
  const [testEmail, setTestEmail] = useState('');
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [scheduleAt, setScheduleAt] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!isNew && detail.data && !ready) {
      setSubject(detail.data.subject);
      setInitialHtml(detail.data.contentHtml);
      setReady(true);
    }
  }, [isNew, detail.data, ready]);

  // Injeta o HTML inicial uma única vez (contenteditable é não-controlado depois disso).
  useEffect(() => {
    if (ready && editorRef.current && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = initialHtml;
    }
  }, [ready, initialHtml]);

  const sent = detail.data?.status === 'SENT' || detail.data?.status === 'SENDING';
  const readOnly = sent;

  function currentHtml(): string {
    return editorRef.current?.innerHTML ?? initialHtml;
  }

  function exec(command: string, arg?: string) {
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, arg);
    editorRef.current?.focus();
  }

  function addLink() {
    const url = window.prompt('Link (comece com https://):');
    if (url && /^https?:\/\//i.test(url)) exec('createLink', url);
  }

  async function ensureSaved(): Promise<string> {
    const payload = { subject: subject.trim(), contentHtml: currentHtml() };
    const data = savedId
      ? await api.updateNewsletter(savedId, payload)
      : await api.createNewsletter(payload);
    setSavedId(data.id);
    return data.id;
  }

  const saveMut = useMutation({
    mutationFn: ensureSaved,
    onSuccess: () => {
      setInfo('Rascunho salvo.');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'newsletters'] });
    },
    onError: (e) => setError(errMsg(e, 'Falha ao salvar')),
  });

  const previewMut = useMutation({
    mutationFn: async () => {
      const nid = await ensureSaved();
      await api.previewNewsletter(nid, { to: testEmail.trim() });
    },
    onSuccess: () => {
      setInfo(`E-mail de teste enviado para ${testEmail}.`);
      setError(null);
    },
    onError: (e) => setError(errMsg(e, 'Falha ao enviar o teste')),
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      const nid = await ensureSaved();
      return api.sendNewsletter(nid, {
        scheduledFor: mode === 'schedule' && scheduleAt ? new Date(scheduleAt).toISOString() : null,
      });
    },
    onSuccess: (data) => {
      setConfirming(false);
      setInfo(
        data.status === 'SCHEDULED'
          ? 'Comunicado agendado.'
          : 'Envio iniciado — acompanhe o status na lista.',
      );
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'newsletters'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'newsletter', id] });
    },
    onError: (e) => {
      setConfirming(false);
      setError(errMsg(e, 'Falha ao enviar'));
    },
  });

  const delMut = useMutation({
    mutationFn: () => api.deleteNewsletter(savedId!),
    onSuccess: onClose,
    onError: (e) => setError(errMsg(e, 'Falha ao excluir')),
  });

  if (!isNew && !ready) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const canSend =
    subject.trim().length >= 3 &&
    currentHtml()
      .replace(/<[^>]+>/g, '')
      .trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="text-brand-600 hover:text-brand-700 text-xs font-semibold"
          >
            ← Voltar
          </button>
          <h1 className="font-display text-ink-900 mt-1 text-2xl font-semibold tracking-tight">
            {isNew ? 'Novo comunicado' : readOnly ? 'Comunicado' : 'Editar comunicado'}
          </h1>
        </div>
        {savedId && !readOnly && (
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 size={14} />}
            loading={delMut.isPending}
            onClick={() => delMut.mutate()}
          >
            Excluir
          </Button>
        )}
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {info && <Alert tone="success">{info}</Alert>}

      <Card>
        <CardBody className="space-y-4">
          <Field label="Assunto" htmlFor="subject" required>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto do e-mail"
              disabled={readOnly}
            />
          </Field>

          <div>
            <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
              Conteúdo
            </label>
            {!readOnly && (
              <div className="mt-1 flex gap-1.5">
                <button
                  type="button"
                  className={EDITOR_BTN}
                  title="Negrito"
                  onClick={() => exec('bold')}
                >
                  <Bold size={14} />
                </button>
                <button
                  type="button"
                  className={EDITOR_BTN}
                  title="Parágrafo"
                  onClick={() => exec('formatBlock', 'p')}
                >
                  <Pilcrow size={14} />
                </button>
                <button type="button" className={EDITOR_BTN} title="Link" onClick={addLink}>
                  <Link2 size={14} />
                </button>
              </div>
            )}
            <div
              ref={editorRef}
              contentEditable={!readOnly}
              suppressContentEditableWarning
              className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1.5 min-h-[200px] w-full rounded-md border bg-white px-3 py-2 text-sm leading-relaxed focus:outline-none"
            />
            <p className="text-2xs text-ink-400 mt-1">
              Use negrito, links e parágrafos. O conteúdo é higienizado no envio.
            </p>
          </div>
        </CardBody>
      </Card>

      {!readOnly && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Enviar teste</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[240px] flex-1">
                  <Field label="E-mail de teste" htmlFor="testemail">
                    <Input
                      id="testemail"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="voce@exemplo.com"
                    />
                  </Field>
                </div>
                <Button
                  variant="secondary"
                  leadingIcon={<Send size={14} />}
                  loading={previewMut.isPending}
                  disabled={!testEmail.trim() || !canSend}
                  onClick={() => previewMut.mutate()}
                >
                  Enviar teste
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Enviar para a base</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'now'}
                    onChange={() => setMode('now')}
                    className="text-brand-600"
                  />
                  Enviar agora
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'schedule'}
                    onChange={() => setMode('schedule')}
                    className="text-brand-600"
                  />
                  Agendar
                </label>
                {mode === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    className="border-ink-200 text-ink-900 focus:border-brand-500 rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
                  />
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  leadingIcon={<Send size={14} />}
                  disabled={!canSend || (mode === 'schedule' && !scheduleAt)}
                  onClick={() => {
                    setError(null);
                    setConfirming(true);
                  }}
                >
                  {mode === 'schedule' ? 'Agendar envio' : 'Enviar agora'}
                </Button>
                <Button
                  variant="ghost"
                  loading={saveMut.isPending}
                  onClick={() => saveMut.mutate()}
                >
                  Salvar rascunho
                </Button>
              </div>

              {confirming && (
                <Alert tone="warning" title="Confirmar envio">
                  <p className="text-sm">
                    Isso vai {mode === 'schedule' ? 'agendar o envio' : 'enviar agora'} para{' '}
                    <strong>toda a base ativa e verificada</strong> (exceto quem cancelou a
                    inscrição). Tem certeza?
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" loading={sendMut.isPending} onClick={() => sendMut.mutate()}>
                      Sim, {mode === 'schedule' ? 'agendar' : 'enviar'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                      Cancelar
                    </Button>
                  </div>
                </Alert>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
